import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { Row, Form, Col, Button, Alert, Badge, Spinner } from 'react-bootstrap';
import { TbCalendarStar } from 'react-icons/tb';
import { IoMdAdd } from 'react-icons/io';
import axiosInstance from '../../Authservices/axiosInstance';

moment.tz.setDefault('Europe/London');

export default function EditAtd() {
  const { attendanceId } = useParams();
  const navigate = useNavigate();

  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [date, setDate] = useState('');
  const [workedHours, setWorkedHours] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [userId, setUserId] = useState('');
  const [shiftStart, setShiftStart] = useState('');
  const [shiftEnd, setShiftEnd] = useState('');
  const [lateMinutes, setLateMinutes] = useState(0);
  const [earlyCloseMinutes, setEarlyCloseMinutes] = useState(0);
  const [extraMinutes, setExtraMinutes] = useState(0);
  const [isSixHourShift, setIsSixHourShift] = useState(false);
  const [countFromActualCheckIn, setCountFromActualCheckIn] = useState(false);
  const [autoBreakMinutes, setAutoBreakMinutes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ===== BREAK EDIT STATES =====
  const [paidBreak, setPaidBreak] = useState(0);
  const [unpaidBreak, setUnpaidBreak] = useState(0);
  const [excessBreak, setExcessBreak] = useState(0);
  const [savingBreak, setSavingBreak] = useState(false);
  const [isAutoBreak, setIsAutoBreak] = useState(false);

  // ✅ FIX: admin ne break edit kiya toh auto-break isko overwrite na kare
  const breakEditedByAdmin = useRef(false);

  useEffect(() => {
    const fetchAttendanceData = async () => {
      try {
        setLoading(true);
        setError(null);

        const attendanceRes = await axiosInstance.get(
          `/attendance/attendance/${attendanceId}`
        );
        const attendanceData = attendanceRes.data;

        if (attendanceData.success) {
          const { checkIn, checkOut, date, userId, workedHours } = attendanceData.data;

          const checkInMoment = moment.utc(checkIn);
          const checkOutMoment = checkOut ? moment.utc(checkOut) : null;

          setStartTime(checkInMoment.tz('Europe/London').format('HH:mm'));
          setEndTime(checkOutMoment ? checkOutMoment.tz('Europe/London').format('HH:mm') : '');
          setDate(moment.utc(date).tz('Europe/London').format('YYYY-MM-DD'));
          setUserId(userId);
          setWorkedHours(workedHours !== null && workedHours !== undefined ? `${workedHours} hrs` : '');

          // ✅ Break fields DB se populate karo
          setPaidBreak(attendanceData.data.totalPaidBreakMinutes ?? 0);
          setUnpaidBreak(attendanceData.data.totalUnpaidBreakMinutes ?? 0);
          setExcessBreak(attendanceData.data.totalExcessUnpaidMinutes ?? 0);
          setIsAutoBreak(attendanceData.data.isAutoBreakDeducted || false);

          // ✅ Fresh load: breaks are not edited yet
          breakEditedByAdmin.current = false;

          // ✅ Timing fetch — 404 ko silently handle karo
          try {
            const timingRes = await axiosInstance.get(`/timings/${userId}`);
            const timingData = timingRes.data;
            const timing = timingData.timing || timingData.data || timingData;

            if (timing && timing.shiftStart && timing.shiftEnd) {
              setShiftStart(timing.shiftStart);
              setShiftEnd(timing.shiftEnd);
              setIsSixHourShift(timing.isSixHourShift || false);
              setCountFromActualCheckIn(timing.countFromActualCheckIn || false);
            }
          } catch (timingErr) {
            console.warn('[EDIT ATD] Timing fetch failed (non-critical):', timingErr.message);
            // ✅ FIX: shift times blank reh jayenge, lekin hours calculation
            // ab actual check-in/check-out se chalega (neeche wala effect)
          }
        } else {
          setError(attendanceData.message || 'Failed to load attendance data');
        }
      } catch (error) {
        console.error('Error fetching attendance:', error);
        setError('Error loading attendance data');
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceData();
  }, [attendanceId]);

  // ✅ FIX: Auto-break sirf DISPLAY ke liye — unpaidBreak ko OVERWRITE nahi karta
  useEffect(() => {
    if (startTime && endTime && date && isSixHourShift) {
      const start = moment.tz(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm', 'Europe/London');
      let end = moment.tz(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm', 'Europe/London');

      if (end.isSameOrBefore(start)) end.add(1, 'day');

      const totalHours = end.diff(start, 'minutes') / 60;

      let autoBreak = 0;
      if (totalHours >= 8) autoBreak = 60;
      else if (totalHours >= 7) autoBreak = 30;

      setAutoBreakMinutes(autoBreak);
      // ❌ YEH LINE HATA DI: setUnpaidBreak(autoBreak);
      // (yehi line admin ki DB se aayi hui / manually edited value mita rahi thi)
    }
  }, [startTime, endTime, date, isSixHourShift]);

  // ✅ FIX: Worked hours preview — shift timing NA bhi mile toh calculate hoga
  useEffect(() => {
    if (!startTime || !endTime || !date) return;

    const start = moment.tz(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm', 'Europe/London');
    let end = moment.tz(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm', 'Europe/London');

    if (end.isSameOrBefore(start)) end.add(1, 'day');

    let effectiveStart = start.clone();
    let effectiveEnd = end.clone();

    // ✅ Cap sirf tab lagao jab shift timing AVAILABLE ho
    // (pehle ye bina timing ke kabhi run nahi hota tha — yahi asli bug tha)
    if (!countFromActualCheckIn && shiftStart && shiftEnd) {
      const officialStart = moment.tz(`${date} ${shiftStart}`, 'YYYY-MM-DD HH:mm', 'Europe/London');
      let officialEnd = moment.tz(`${date} ${shiftEnd}`, 'YYYY-MM-DD HH:mm', 'Europe/London');
      if (officialEnd.isSameOrBefore(officialStart)) officialEnd.add(1, 'day');

      if (effectiveStart.isBefore(officialStart)) effectiveStart = officialStart.clone();
      if (effectiveEnd.isAfter(officialEnd)) effectiveEnd = officialEnd.clone();
    }

    let effectiveMinutes = 0;
    if (effectiveEnd.isAfter(effectiveStart)) {
      effectiveMinutes = effectiveEnd.diff(effectiveStart, 'minutes');
    }

    const deduction = (Number(unpaidBreak) || 0) + (Number(excessBreak) || 0);
    const netMinutes = Math.max(0, effectiveMinutes - deduction);

    setWorkedHours(`${(netMinutes / 60).toFixed(2)} hrs`);

    // Late/Early/Overtime — sirf jab shift timing ho
    if (shiftStart && shiftEnd) {
      const officialStart = moment.tz(`${date} ${shiftStart}`, 'YYYY-MM-DD HH:mm', 'Europe/London');
      let officialEnd = moment.tz(`${date} ${shiftEnd}`, 'YYYY-MM-DD HH:mm', 'Europe/London');
      if (officialEnd.isSameOrBefore(officialStart)) officialEnd.add(1, 'day');

      setLateMinutes(Math.max(0, start.diff(officialStart, 'minutes')));
      setEarlyCloseMinutes(Math.max(0, officialEnd.diff(end, 'minutes')));

      let extraMin = 0;
      if (end.isAfter(officialEnd)) {
        const overtimeDiff = end.diff(officialEnd, 'minutes');
        if (overtimeDiff >= 30) extraMin = overtimeDiff;
      }
      setExtraMinutes(extraMin);
    }
  }, [startTime, endTime, date, shiftStart, shiftEnd, unpaidBreak, excessBreak, countFromActualCheckIn]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!startTime || !endTime || !date) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      // ✅ FIX: Break values are sent ONLY if the admin actually edited them.
      // Before, they were always sent, so a time-only edit turned an
      // auto-deducted break into a "manual" one and the break modal hid it.
      const payload = {
        checkIn: startTime,
        checkOut: endTime,
        date,
      };

      if (breakEditedByAdmin.current) {
        payload.totalPaidBreakMinutes = Number(paidBreak) || 0;
        payload.totalUnpaidBreakMinutes = Number(unpaidBreak) || 0;
        payload.totalExcessUnpaidMinutes = Number(excessBreak) || 0;
      }

      const response = await axiosInstance.put(
        `/attendance/attendance/edit/${attendanceId}`,
        payload
      );
      const result = response.data;

      if (result.success) {
        console.log('[UPDATE ATD] Success! New worked hours:', result.data.workedHours);
        setWorkedHours(`${result.data.workedHours} hrs`);
        setShowAlert(true);
        setTimeout(() => navigate(`/current-month-atd/${userId}`), 2000);
      } else {
        alert(result.message || 'Error updating attendance.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error updating attendance: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBreak = async () => {
    if (savingBreak) return;

    // ✅ Admin ne manually edit ki — auto-break ab override na kare
    breakEditedByAdmin.current = true;

    setSavingBreak(true);
    try {
      const response = await axiosInstance.put(
        `/attendance/attendance/${attendanceId}/edit-break`,
        {
          totalPaidBreakMinutes: Number(paidBreak) || 0,
          totalUnpaidBreakMinutes: Number(unpaidBreak) || 0,
          totalExcessUnpaidMinutes: Number(excessBreak) || 0,
          totalBreakMinutes:
            (Number(paidBreak) || 0) +
            (Number(unpaidBreak) || 0) +
            (Number(excessBreak) || 0),
        }
      );

      const result = response.data;

      if (result.success) {
        const updated = result.data;
        // ✅ DEBUG: server se kya hours aaye, console mein dekho
        console.log('[EDIT BREAK] New workedHours from server:', updated.workedHours);

        setWorkedHours(`${updated.workedHours} hrs`);
        setIsAutoBreak(updated.isAutoBreakDeducted || false);
        setPaidBreak(updated.totalPaidBreakMinutes ?? 0);
        setUnpaidBreak(updated.totalUnpaidBreakMinutes ?? 0);
        setExcessBreak(updated.totalExcessUnpaidMinutes ?? 0);
        alert('Break minutes updated successfully! Worked hours adjusted.');
      } else {
        alert(result.message || 'Failed to update break.');
      }
    } catch (error) {
      console.error('Error updating break:', error);
      alert('Error: ' + (error.response?.data?.message || error.message));
    } finally {
      setSavingBreak(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading attendance data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <h1 className="add-attendance-title">
          <TbCalendarStar /> Edit Attendance
        </h1>
        <hr />
        <Alert variant="danger" className="m-3">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <h1 className="add-attendance-title">
        <TbCalendarStar /> Edit Attendance
      </h1>
      <hr />
      <div className="add-attendance-container">
        {showAlert && (
          <Alert variant="success" onClose={() => setShowAlert(false)} dismissible>
            Attendance updated successfully! Redirecting...
          </Alert>
        )}

        {!shiftStart && (
          <Alert variant="warning">
            ⚠️ Shift timing not found for this user. Worked hours are calculated
            from actual check-in/check-out only.
          </Alert>
        )}

        {shiftStart && shiftEnd && (
          <Alert variant="info">
            <strong>Official Shift:</strong> {shiftStart} to {shiftEnd}
            {isSixHourShift && <Badge bg="info" className="ms-2">6-Hour Shift</Badge>}
            {autoBreakMinutes > 0 && <Badge bg="warning" className="ms-2">Auto Break: {autoBreakMinutes}m</Badge>}
            {lateMinutes > 0 && <Badge bg="warning" className="ms-2">Late: {lateMinutes}m</Badge>}
            {earlyCloseMinutes > 0 && <Badge bg="danger" className="ms-2">Early: {earlyCloseMinutes}m</Badge>}
            {extraMinutes > 0 && <Badge bg="success" className="ms-2">Overtime: {extraMinutes}m</Badge>}
          </Alert>
        )}

        <Row className="mb-3">
          <Form.Group as={Col} xs={12} md={3}>
            <Form.Label>Date <span className="required">*</span>:</Form.Label>
            <Form.Control type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Form.Group>

          <Form.Group as={Col} xs={12} md={3}>
            <Form.Label>Start Time <span className="required">*</span>:</Form.Label>
            <Form.Control type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </Form.Group>

          <Form.Group as={Col} xs={12} md={3}>
            <Form.Label>End Time <span className="required">*</span>:</Form.Label>
            <Form.Control type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </Form.Group>
        </Row>

        <Row className="mb-3">
          <Form.Group as={Col} xs={12} md={3}>
            <Form.Label>Worked Hours:</Form.Label>
            <Form.Control type="text" value={workedHours} readOnly />
          </Form.Group>

          {isSixHourShift && autoBreakMinutes > 0 && (
            <Form.Group as={Col} xs={12} md={3}>
              <Form.Label>Auto Break (info only):</Form.Label>
              <Form.Control type="text" value={`${autoBreakMinutes} minutes`} readOnly />
            </Form.Group>
          )}
        </Row>

        {/* ========== BREAK ADJUSTMENT SECTION ========== */}
        <div
          className="p-3 mt-3"
          style={{
            background: 'linear-gradient(135deg, #e7f1ff, #cfe2ff)',
            border: '2px solid #0d6efd',
            borderRadius: '12px',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0" style={{ color: '#084298', fontWeight: '700' }}>
              ⏸️ Break Minutes
              {isAutoBreak && (
                <Badge bg="primary" text="light" className="ms-2">Auto-Deducted</Badge>
              )}
            </h5>
            <span style={{ color: '#084298', fontSize: '13px', fontWeight: '600' }}>
              Total: {(Number(paidBreak) || 0) + (Number(unpaidBreak) || 0) + (Number(excessBreak) || 0)} min
            </span>
          </div>

          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={3}>
              <Form.Label style={{ color: '#084298', fontWeight: '600' }}>Paid Break (min):</Form.Label>
              <Form.Control
                type="number" min="0" value={paidBreak}
                onChange={(e) => { setPaidBreak(e.target.value); breakEditedByAdmin.current = true; }}
                style={{ border: '2px solid #0d6efd' }}
              />
              <Form.Text className="text-muted">Does not deduct from hours</Form.Text>
            </Form.Group>

            <Form.Group as={Col} xs={12} md={3}>
              <Form.Label style={{ color: '#084298', fontWeight: '600' }}>Unpaid Break (min):</Form.Label>
              <Form.Control
                type="number" min="0" value={unpaidBreak}
                onChange={(e) => { setUnpaidBreak(e.target.value); breakEditedByAdmin.current = true; }}
                style={{ border: '2px solid #0a58ca' }}
              />
              <Form.Text style={{ color: '#0a58ca' }}>Deducts from worked hours</Form.Text>
            </Form.Group>

            <Form.Group as={Col} xs={12} md={3}>
              <Form.Label style={{ color: '#084298', fontWeight: '600' }}>Excess Unpaid (min):</Form.Label>
              <Form.Control
                type="number" min="0" value={excessBreak}
                onChange={(e) => { setExcessBreak(e.target.value); breakEditedByAdmin.current = true; }}
                style={{ border: '2px solid #084298' }}
              />
              <Form.Text className="text-danger">Deducts from worked hours</Form.Text>
            </Form.Group>

            <Form.Group as={Col} xs={12} md={3}>
              <Form.Label style={{ color: '#084298', fontWeight: '600' }}>Total Deduction:</Form.Label>
              <Form.Control
                type="text" readOnly
                value={`${(Number(unpaidBreak) || 0) + (Number(excessBreak) || 0)} minutes`}
                style={{ backgroundColor: '#cfe2ff', border: '2px solid #0d6efd', fontWeight: '700', color: '#084298' }}
              />
            </Form.Group>
          </Row>

          <Button variant="primary" onClick={handleSaveBreak} disabled={savingBreak} style={{ fontWeight: '700' }}>
            {savingBreak ? (
              <><Spinner animation="border" size="sm" className="me-2" /> Saving...</>
            ) : (
              '💾 Save Break Minutes'
            )}
          </Button>
        </div>

        <Button className="btn-primary mt-3" onClick={handleSubmit} disabled={saving}>
          {saving ? (
            <><Spinner animation="border" size="sm" className="me-2" /> Updating...</>
          ) : (
            <><IoMdAdd /> Update Attendance</>
          )}
        </Button>
      </div>
    </div>
  );
}